/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import { Chevron as IconChevron } from '@ahamove/icons';
import { Path as IconPath, Pickup as IconPickup } from '@ahamove/icons/map';
import { IndexedPath as IconIndexedPath } from '@ahamove/icons/marker';
// import { Hamburger as IconHamburger } from '@ahamove/icons/order';
import {
  getAddressLabels,
  type Address,
  type Path,
} from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { useLocationStore } from '@ahm/react-map/store';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  Title,
  rem,
  type BadgeProps,
  type ButtonProps,
} from '@mantine/core';
import { openContextModal } from '@mantine/modals';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import AddPathBtn from './add-path-btn';
import classes from './path-btn.module.css';
import IconHamburger from '@/assets/icons/hamburger-icon.svg';
import { ModalName } from '@/components/modals';
import { ROUTES } from '@/config/router';
import { ActionIndexPath } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { getMaxStopPointsConfig, getServiceType } from '@/lib/utils';
import { usePathname, useRouter } from '@/navigation';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type Props = ButtonProps & {
  dndMode?: boolean;
  path?: Path;
  pathIndex: number;
  onClick?: () => void;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function PathBtn({ dndMode, path, pathIndex, onClick }: Props) {
  const tBooking = useTranslations('booking');
  const isMobile = useIsMobile();
  const router = useRouter();
  const pathname = usePathname();

  const removePath = useLocationStore.use.removePath();
  const updatePathByIndex = useLocationStore.use.updatePathByIndex();
  const paths = useLocationStore.use.paths();
  const { selectedService } = useBookingStore((state) => state);
  const { isNationwide } = useMemo(() => getServiceType(pathname), [pathname]);
  const handleClearPath = () => {
    removePath(pathIndex);
  };

  const maxStopPoints = useMemo(() => {
    return getMaxStopPointsConfig(selectedService);
  }, [selectedService]);

  const initialAddress = useMemo(() => {
    if (!path) return;

    return {
      ...path,
      address: path.address,
      first_label: getAddressLabels(path.address)[0],
      second_label: getAddressLabels(path.address)[1],
      lat: path.lat,
      lng: path.lng,
      level: path?.level,
      remarks: path?.remarks,
    } as Address;
  }, [path]);

  const handleOpenPathModal = () => {
    openContextModal({
      id: ModalName.Path,
      modal: ModalName.Path,
      innerProps: {
        pathIndex,
        path,
        selectedAddress: initialAddress,
        onConfirm: handleConfirmPath,
      },
      fullScreen: true,
      withCloseButton: false,
      scrollAreaComponent: ScrollArea.Autosize,
      classNames: {
        content:
          '[&>div]:w-full [&>div>div]:w-full [&_.ahm-ScrollArea-viewport>div]:!block',
      },
      styles: {
        content: {
          backgroundColor: 'var(--mantine-color-neutral-0)',
        },
        body: {
          position: 'relative',
          padding: 0,
        },
      },
    });
  };

  const handleConfirmPath = (path: Path) => {
    const newPath = {
      ...path,
    };
    updatePathByIndex(newPath, pathIndex);

    if (
      [ActionIndexPath.AddMoreBtn, ActionIndexPath.ShowInput].includes(
        pathIndex
      ) &&
      paths.length >= 1 &&
      isMobile &&
      pathname === ROUTES.home
    ) {
      router.push(ROUTES.newOrder);
    }
  };

  const isLongPath = useMemo(() => {
    if (!path) return false;
    return (
      (path.tracking_number ||
        path.cod ||
        path.item_value ||
        path.inspection_policies) &&
      pathIndex > 0
    );
  }, [path]);

  if (pathIndex === ActionIndexPath.AddMoreBtn) {
    return (
      <AddPathBtn
        onClick={() => {
          if (paths && paths.length > maxStopPoints) return;
          handleOpenPathModal();
        }}
      />
    );
  }

  return (
    <>
      <Button
        onClick={() => {
          if (dndMode) return;

          if (typeof onClick === 'function') {
            onClick();
            return;
          }
          handleOpenPathModal();
        }}
        component="div"
        fullWidth
        variant="white"
        color="neutral"
        px="md"
        py={rem(6)}
        h="auto"
        mih={rem(56)}
        radius={rem(12)}
        title={path?.address}
        justify="space-between"
        classNames={{
          ...classes,
          root: ` ${dndMode ? 'sortable-handler' : ''}`,
        }}
        leftSection={
          <Box
            style={{
              backgroundColor: 'var(--mantine-color-white)',
              padding: `${rem(3)} 0`,
              position: 'relative',
              zIndex: 1,
            }}
          >
            {pathIndex === 0 ? (
              <IconPickup width={16} height={16} />
            ) : pathIndex > 0 && paths.length > 2 ? (
              <IconIndexedPath
                width={16}
                height={16}
                value={pathIndex === ActionIndexPath.ShowInput ? 0 : pathIndex}
              />
            ) : (
              <IconPath width={16} height={16} />
            )}
          </Box>
        }
        rightSection={
          dndMode ? (
            <ActionIcon
              variant="transparent"
              size={rem(24)}
              // className="sortable-handler"
              flex="none"
            >
              <IconHamburger fill="#757D86" width={24} height={24} />
            </ActionIcon>
          ) : pathIndex === ActionIndexPath.Pickup ? (
            <ThemeIcon size={24} variant="transparent" flex="none">
              <IconChevron height={24} width={24} />
            </ThemeIcon>
          ) : path ? (
            <ActionIcon
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation();
                handleClearPath();
              }}
              title="Clear"
              flex="none"
              size={rem(24)}
              c="neutral.4"
            >
              <X width={24} height={24} />
            </ActionIcon>
          ) : undefined
        }
        styles={{
          root: {
            border: 'none',
            cursor: dndMode ? 'move' : 'pointer',
          },
          label: {
            width: '100%',
            paddingRight: rem(6),
            paddingLeft: rem(6),
          },
        }}
      >
        <Stack
          gap={rem(isMobile ? 2 : 4)}
          flex="1"
          align="flex-start"
          content="top"
          justify="center"
          // mih={rem(56 - 6 - 6)}
          style={{ whiteSpace: 'pre-line' }}
        >
          {path ? (
            <>
              <Title
                order={isMobile ? 6 : 4}
                c="neutral.7"
                lineClamp={1}
                style={{ textAlign: 'left' }}
              >
                {path.first_label}
              </Title>
              {path.name || path.mobile ? (
                <Text
                  ta="left"
                  size={isMobile ? 'sm' : ' md'}
                  fw={500}
                  c="neutral.7"
                >
                  {path.name ?? null}
                  {path.name && path.mobile ? ' | ' : null}
                  {path.mobile ?? null}
                </Text>
              ) : pathIndex === ActionIndexPath.Pickup ? null : (
                <Text ta="left" size="sm" fw={700} c="secondary.5">
                  {tBooking('location.add_location_details')}{' '}
                  {pathIndex !== ActionIndexPath.Pickup && isNationwide && (
                    <Text
                      lh="xs"
                      size="sm"
                      fw={700}
                      c="red.4"
                      component="span"
                      styles={{
                        root: {
                          verticalAlign: 'middle',
                        },
                      }}
                    >
                      *
                    </Text>
                  )}
                </Text>
              )}

              {isLongPath ? (
                <Group gap={rem(isMobile ? 4 : 8)}>
                  {path.cod && path.cod > 0 ? (
                    <PathBtnBadge>
                      COD:{' '}
                      <span
                        style={{
                          textTransform: 'none',
                        }}
                      >
                        {formatCurrency(path.cod)}
                      </span>
                    </PathBtnBadge>
                  ) : null}
                  {path.item_value ? (
                    <PathBtnBadge>
                      <span
                        style={{
                          textTransform: 'none',
                        }}
                      >
                        {tBooking('insurance.item_value_price')}:{' '}
                        {formatCurrency(path.item_value)}
                      </span>
                    </PathBtnBadge>
                  ) : null}
                  {path.tracking_number && (
                    <PathBtnBadge>{path.tracking_number}</PathBtnBadge>
                  )}
                  {path.inspection_policies?.map((p, idx) => (
                    <PathBtnBadge key={idx}>
                      {p === 'ALLOW_TO_INSPECT_GOODS'
                        ? tBooking('location.check')
                        : tBooking('location.test')}
                    </PathBtnBadge>
                  ))}
                </Group>
              ) : null}
            </>
          ) : (
            <Title ta="left" order={4} c="neutral.4">
              {tBooking(
                pathIndex === ActionIndexPath.Pickup
                  ? 'location.where_do_you_want_to_pickup'
                  : 'location.where_do_you_send'
              )}
            </Title>
          )}
        </Stack>
      </Button>
    </>
  );
}

export function PathBtnBadge({ children, ...props }: BadgeProps) {
  const isMobile = useIsMobile();

  return (
    <Badge
      size={isMobile ? 'md' : 'lg'}
      h={rem(isMobile ? 18 : 32)}
      fw={600}
      variant="light"
      bg="neutral.1"
      c="neutral.7"
      radius={rem(2)}
      {...props}
    >
      {children}
    </Badge>
  );
}
